export class ExifUtils {
  // Simple parser to extract Make, Model, DateTime
  // This is a minimal implementation.
  // data should be the payload of APP1 (JPEG) or EXIF chunk (WebP).
  // Assumes 'Exif\0\0' header is stripped or handled?
  // JPEG APP1: Exif\0\0 + TIFF Header
  // WebP EXIF: Exif\0\0 + TIFF Header? Or just TIFF Header?
  // Usually starts with 'Exif\0\0' (6 bytes)

  static parse(data: Uint8Array): Record<string, string> {
    const meta: Record<string, string> = {};
    if (data.length < 14) return meta;

    let offset = 0;
    // Check for 'Exif\0\0'
    const header = String.fromCharCode(...data.slice(0, 6));
    if (header === 'Exif\0\0') {
      offset = 6;
    }

    // TIFF Header
    // 0-1: Byte Order (II or MM)
    // 2-3: 42 (0x2A00 or 0x002A)
    // 4-7: Offset to 0th IFD

    if (offset + 8 > data.length) return meta;

    const bigEndian = data[offset] === 0x4D && data[offset + 1] === 0x4D; // MM
    const view = new DataView(data.buffer, data.byteOffset + offset, data.byteLength - offset);

    // Validate TIFF
    const fortyTwo = view.getUint16(2, !bigEndian);
    if (fortyTwo !== 42) return meta;

    const ifd0Offset = view.getUint32(4, !bigEndian);
    if (ifd0Offset < 8 || ifd0Offset > data.length) return meta;

    // Parse IFD0
    this.parseIFD(view, ifd0Offset, meta, bigEndian);

    // TODO: Parse Exif IFD? (tag 0x8769)
    // For now, Maker and Model are usually in IFD0.

    return meta;
  }

  private static parseIFD(view: DataView, offset: number, meta: Record<string, string>, bigEndian: boolean) {
    if (offset + 2 > view.byteLength) return;
    const numEntries = view.getUint16(offset, !bigEndian);
    let current = offset + 2;

    for (let i = 0; i < numEntries; i++) {
      if (current + 12 > view.byteLength) break;
      const tag = view.getUint16(current, !bigEndian);
      const type = view.getUint16(current + 2, !bigEndian);
      const count = view.getUint32(current + 4, !bigEndian);
      // const valueOffset = view.getUint32(current + 8, !bigEndian); // Value or offset

      // Tags:
      // 0x010F: Make
      // 0x0110: Model
      // 0x0132: DateTime
      // 0x8769: ExifOffset

      if (tag === 0x010F) meta['Make'] = this.readTagValue(view, current + 8, type, count, bigEndian);
      if (tag === 0x0110) meta['Model'] = this.readTagValue(view, current + 8, type, count, bigEndian);
      if (tag === 0x0132) meta['Date'] = this.readTagValue(view, current + 8, type, count, bigEndian);

      current += 12;
    }
  }

  private static readTagValue(view: DataView, offsetFieldOffset: number, type: number, count: number, bigEndian: boolean): string {
    // Type 2: ASCII
    if (type !== 2) return '';

    let valOffset = view.getUint32(offsetFieldOffset, !bigEndian);
    // If fits in 4 bytes, it's stored directly?
    // TIFF spec: if count * size <= 4, value is in offset field.
    // ASCII (1 byte) -> if count <= 4

    if (count <= 4) {
      valOffset = offsetFieldOffset;
    }

    if (valOffset + count > view.byteLength) return '';

    let str = '';
    for (let i = 0; i < count; i++) {
      const charCode = view.getUint8(valOffset + i);
      if (charCode !== 0) str += String.fromCharCode(charCode);
    }
    return str.trim();
  }
}
